/* eslint-disable max-lines */
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Drawer } from 'antd';
import { Icon } from '@fuxi/eevee-icon';
import dayjs, { Dayjs } from 'dayjs';
import { CloseCircleFilled } from '@ant-design/icons';
import { Button, FloatingPanel, FloatingPanelRef, PickerView } from 'antd-mobile';
import { useGetState, useLatest, useMemoizedFn, useUpdateEffect } from 'ahooks';
import { Toast } from 'common-package';
import { DAYJS_FORMAT } from '@/constant';
import ScrollView, { ScrollViewRefAttrs } from '../scroll-view';
import cx from './index.module.scss';

enum FloatingPanelHeight {
  Expand = 299,
  Close = 151,
}

export interface CalendarProps {
  value: Dayjs | [Dayjs, Dayjs];
  onChange: (date: CalendarProps['value']) => void;
  mode?: 'date' | 'range' | 'minutes';
  placeholder?: string;
  className?: string;
  disabledDate?: (currentDate: Dayjs) => boolean;
  allowClear?: boolean;
  cancelSelected?: boolean;
}

// 获取当前年度每月的第一天的数组集合
const getFirstDayInEveryMonth = (date: Dayjs) => {
  if (!date) return [];
  const currentDate = date.year(date.year()).month(0).date(1);

  const allMonthInYear: Dayjs[] = [];

  // 遍历12个月，分别获取每个月的第一天
  for (let i = 0; i < 12; i++) {
    const firstDayOfMonth = currentDate.startOf('month').add(i, 'months');

    allMonthInYear.push(firstDayOfMonth);
  }

  return allMonthInYear;
};

const Calendar: FC<CalendarProps> = props => {
  const {
    value,
    onChange,
    placeholder,
    className,
    disabledDate,
    mode = 'date',
    allowClear = true,
    cancelSelected = false,
  } = props || {};

  const [isShowYearContent, setIsShowYearContent] = useState(false);
  const [isDrawerShow, setIsDrawerShow] = useState(false);
  const [date, setDate, getDate] = useGetState<Dayjs>(
    (['date', 'minutes'].includes(mode) ? value : value?.[0]) || dayjs()
  );
  const scrollViewRef = useRef<ScrollViewRefAttrs>(null);
  const [update, setUpdate] = useState(0);
  const [isFloatingPanelOpen, setIsFloatingPanelOpen] = useState(false);
  const latestValue = useLatest(value);
  const [pickerValue, setPickerValue] = useState<string[]>(['0', '00']);
  const floatingPanelRef = useRef<FloatingPanelRef>(null);

  const timeTextColumn = useMemo(
    () => [
      new Array(24).fill('').map((_, index) => ({
        value: `${index}`,
        label: `${index}`,
      })),
      new Array(60).fill('').map((_, index) => ({
        value: `${index}`.padStart(2, '0'),
        label: `${index}`.padStart(2, '0'),
      })),
    ],
    []
  );

  // 通过日期生成当月月历
  const genMonthCalendar = useCallback(
    (date: Dayjs) => {
      if (!date) return null;
      // 当前月份有几天
      const daysInMonth = new Array(date.daysInMonth()).fill(0).map((_, i) => date.set('date', i + 1));

      return (
        <>
          <div className={cx('month-title')}>{!!date ? date.format('M月') : ''}</div>
          <div className={cx('month-detail')}>
            {[
              // 当前月份第一天是星期几
              ...new Array(date.startOf('month').day()).fill(0).map((_, i) => <div key={i} className={cx('day')} />),
              ...daysInMonth.map(day => {
                // 当前是否是选中的开始日期
                const currentIsStartDate =
                  mode === 'range'
                    ? value?.[0] && value?.[0].isSame(day, 'date')
                    : value && (value as Dayjs).isSame(day, 'date');

                // 当前是否是选中的结束日期
                const currentIsEndDate = mode === 'range' && value?.[1] && value?.[1].isSame(day, 'date');

                // 当前日期是否在开始和结束日期之间
                const isInRange =
                  mode === 'range' &&
                  value?.[0] &&
                  value?.[1] &&
                  ((day.startOf('date').isAfter(value[0]) && day.endOf('date').isBefore(value[1])) ||
                    // date2有可能在date1之前
                    (day.startOf('date').isAfter(value[1]) && day.endOf('date').isBefore(value[0])));

                const currentIsDisabled = !!disabledDate && disabledDate?.(day);

                return (
                  <div
                    key={day.format(DAYJS_FORMAT.DATE)}
                    className={cx('day-container', {
                      'is-start': currentIsStartDate,
                      'is-end': currentIsEndDate,
                      'in-range': isInRange,
                      'is-disabled': currentIsDisabled,
                      'is-start-noRange': currentIsStartDate && mode === 'date',
                    })}
                  >
                    <div
                      data-set={day.format(DAYJS_FORMAT.DATE)}
                      onClick={() => {
                        if (currentIsDisabled) return;

                        if (mode === 'date') {
                          onChange?.(day);
                        } else if (mode === 'range') {
                          if (value?.[0] && value?.[0].isSame(day, 'date')) {
                            if (cancelSelected) {
                              onChange(null);
                            } else {
                              Toast.show('不可选择同一开始日期');
                            }
                            return;
                          }
                          if (!value?.[0]) {
                            onChange?.([day, null]);
                          } else if (!value?.[1]) {
                            onChange([value?.[0], day]);
                          } else {
                            onChange([day, null]);
                          }
                        } else if (mode === 'minutes') {
                          if (value) {
                            onChange(
                              dayjs(`${(day as Dayjs)?.format('YYYY-MM-DD')} ${(value as Dayjs).format('HH:mm')}`)
                            );
                          } else {
                            // 传进来的 value 值为空 兜底处理
                            onChange(day);
                          }
                        }
                      }}
                      className={cx('day')}
                    >
                      {/* {mode === 'range' && currentIsStartDate && <div className={cx('label')}>开始</div>}
                      {mode === 'range' && currentIsEndDate && <div className={cx('label')}>结束</div>} */}
                      <div className={cx('day-text')}>{day.format('D')}</div>
                    </div>
                  </div>
                );
              }),
            ]}
          </div>
        </>
      );
    },
    [cancelSelected, disabledDate, mode, onChange, value]
  );

  // 生成年份列表
  const genYearLists = useCallback(() => {
    const yearArrayList = Array.from({ length: 101 }, (_, index) => dayjs().year() - 100 + index).map(item =>
      dayjs().set('year', item).startOf('year')
    );

    return (
      <div className={cx('year-lists')}>
        {yearArrayList.map(item => {
          const currentIsDisabled = !!disabledDate && disabledDate?.(item);
          return (
            <div
              data-set={item.format(DAYJS_FORMAT.YEAR)}
              key={item.format(DAYJS_FORMAT.YEAR)}
              className={cx('year-item', {
                'current-year': date.year() === item.year(),
                'is-disabled': currentIsDisabled,
              })}
              onClick={() => {
                if (currentIsDisabled) return;

                setDate(item);
                setIsShowYearContent(false);
              }}
            >
              {item.format(DAYJS_FORMAT.YEAR)}
            </div>
          );
        })}
      </div>
    );
  }, [date, disabledDate, setDate]);

  const handleCloseDrawer = useCallback(() => {
    setIsDrawerShow(false);
    setIsShowYearContent(false);
  }, []);
  // 日期触发滑动事件,时间组件关闭
  const onScrollEvent = useMemoizedFn(() => {
    // 处理滑动事件的逻辑
    if (mode === 'minutes' && isFloatingPanelOpen) {
      floatingPanelRef.current?.setHeight(FloatingPanelHeight.Close);
    }
  });

  const isSubYearBtnDisabled = useMemo(() => date.year() <= dayjs().year() - 100, [date]);
  const isAddYearBtnDisabled = useMemo(() => date.year() >= dayjs().year(), [date]);

  // pickerValue 改变时,触发onChange
  useUpdateEffect(() => {
    onChange?.(dayjs(`${(latestValue.current as Dayjs)?.format('YYYY-MM-DD')} ${pickerValue?.join(':')}`));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickerValue]);

  useEffect(() => {
    if (isDrawerShow) {
      setDate((['date', 'minutes'].includes(mode) ? latestValue.current : latestValue.current?.[0]) || dayjs());
      if (mode === 'minutes') {
        setTimeout(() => {
          floatingPanelRef.current?.setHeight(FloatingPanelHeight.Expand);
        }, 300);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDrawerShow, mode]);

  // hack 在同一更新周期无法拿到scrollView的bsInstance的问题
  useEffect(() => {
    if (isDrawerShow) {
      setIsFloatingPanelOpen(false);
      setUpdate(count => count + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDrawerShow]);

  useEffect(() => {
    const dateFormat = isShowYearContent ? DAYJS_FORMAT.YEAR : DAYJS_FORMAT.DATE;
    const value: Dayjs = (mode === 'range' ? latestValue.current?.[0] : latestValue.current) || dayjs();

    const element = document.querySelector(`div[data-set='${value.format(dateFormat)}']`) as HTMLElement;

    !!element && setTimeout(() => scrollViewRef.current?.bsInstance.scrollToElement(element, 0, 0, -160), 10);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [update, isShowYearContent]);

  useEffect(() => {
    scrollViewRef.current?.handleRefresh();
  }, [isShowYearContent]);

  // 时间选择器打开时，设置当前时间
  useEffect(() => {
    if (mode !== 'minutes') return;
    if (isFloatingPanelOpen && latestValue.current) {
      setPickerValue((latestValue.current as Dayjs)?.format('H:mm')?.split(':') || []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFloatingPanelOpen, mode]);

  return (
    <div className={cx('calendar-container', className)}>
      <div className={cx('calendar-input')} onClick={() => setIsDrawerShow(true)}>
        {mode === 'date' || mode === 'minutes' ? (
          <div className={cx('mode-date')}>
            <div className={cx('value-content')}>
              {value ? (
                <div>
                  {dayjs(value as Dayjs).format(mode === 'date' ? DAYJS_FORMAT.DATE : DAYJS_FORMAT.MINUTESCHINESE)}
                </div>
              ) : (
                <div className={cx('date-placeholder')}>{placeholder}</div>
              )}
            </div>

            {mode === 'date' ? (
              value && allowClear ? (
                <CloseCircleFilled
                  style={{ color: '#b3b4b6', fontSize: '20px' }}
                  onClick={e => {
                    e.stopPropagation();
                    onChange(null);
                  }}
                />
              ) : (
                <Icon name="calendar" className={cx('calendar-icon')} />
              )
            ) : (
              <Icon name="arrow-right" className={cx('icon')} />
            )}
          </div>
        ) : (
          <div className={cx('mode-range')}>
            <div className={cx('value-content')}>
              <div className={cx('time-text')}>
                {value?.[0] ? (
                  <div>{dayjs(value?.[0] as Dayjs).format(DAYJS_FORMAT.DATE)}</div>
                ) : (
                  <div className={cx('date-placeholder')}>开始时间</div>
                )}
              </div>
              <div className={cx('between')}>至</div>
              <div className={cx('time-text')}>
                {value?.[1] ? (
                  <div>{dayjs(value?.[1] as Dayjs).format(DAYJS_FORMAT.DATE)}</div>
                ) : (
                  <div className={cx('date-placeholder')}>结束时间</div>
                )}
              </div>
            </div>
            {value?.[0] && allowClear ? (
              <CloseCircleFilled
                style={{ color: '#b3b4b6', fontSize: '20px' }}
                onClick={e => {
                  e.stopPropagation();
                  onChange(null);
                }}
              />
            ) : (
              <Icon name="calendar" className={cx('calendar-icon')} />
            )}
          </div>
        )}
      </div>
      <Drawer
        className={cx('calendar-drawer')}
        onClose={handleCloseDrawer}
        width="520px"
        destroyOnClose={true}
        title={mode === 'minutes' ? '选择时间' : '选择日期'}
        closeIcon={<Icon name="close-circle" color="#b3b4b6" className={cx('close-drawer')} />}
        open={isDrawerShow}
      >
        <div className={cx('calendar')}>
          {mode === 'minutes' && (
            <div className={cx('floating-panel', { hide: isShowYearContent })}>
              <FloatingPanel
                handleDraggingOfContent={false}
                anchors={[151, 299]}
                ref={floatingPanelRef}
                className={cx({ open: isFloatingPanelOpen })}
                onHeightChange={(height, animating) => {
                  if (!animating) {
                    setIsFloatingPanelOpen(height > FloatingPanelHeight.Close);
                  }
                }}
              >
                <div
                  className={cx('floating-panel-header')}
                  onClick={() => {
                    floatingPanelRef.current?.setHeight(
                      isFloatingPanelOpen ? FloatingPanelHeight.Close : FloatingPanelHeight.Expand
                    );
                  }}
                />
                <div className={cx('time-text')}>{(value as Dayjs)?.format('MM月DD日 HH:mm')}</div>
                <div className={cx('time-picker-view')}>
                  {isFloatingPanelOpen && (
                    <div className={cx('picker-view-box')}>
                      <PickerView
                        columns={timeTextColumn}
                        value={pickerValue}
                        onChange={v => setPickerValue(v as any)}
                      />
                      <div className={cx('mask')}>
                        <div className={cx('mask-top')}></div>
                        <div className={cx('mask-bottom')}></div>
                      </div>
                    </div>
                  )}
                </div>
              </FloatingPanel>
            </div>
          )}
          <div className={cx('header-container')}>
            <div className={cx('year-container', { show: isShowYearContent })}>
              {!isShowYearContent && (
                <Icon
                  className={cx('icon', { disabled: isSubYearBtnDisabled })}
                  name="calendar-left"
                  onClick={() => !isSubYearBtnDisabled && setDate(date.subtract(1, 'years'))}
                />
              )}
              <div className={cx('year-text')}>
                <span onClick={() => setIsShowYearContent(!isShowYearContent)}>{date.format('YYYY年')}</span>
              </div>
              {!isShowYearContent && (
                <Icon
                  className={cx('icon', { disabled: isAddYearBtnDisabled })}
                  name="calendar-right"
                  onClick={() => !isAddYearBtnDisabled && setDate(date.add(1, 'years'))}
                />
              )}
            </div>
            {!isShowYearContent && (
              <div className={cx('weeks')}>
                <div>日</div>
                <div>一</div>
                <div>二</div>
                <div>三</div>
                <div>四</div>
                <div>五</div>
                <div>六</div>
              </div>
            )}
          </div>
          <ScrollView className={cx('view-container')} ref={scrollViewRef} onScrollEvent={onScrollEvent}>
            <div className={cx('view-wrapper')}>
              {isShowYearContent ? (
                <div className={cx('year-content-wrapper')}>{genYearLists()}</div>
              ) : (
                <div className={cx('month-lists')}>
                  {getFirstDayInEveryMonth(date).map(item => (
                    <div key={item.format(DAYJS_FORMAT.MONTH)} className={cx('month-item')}>
                      {genMonthCalendar(dayjs(item))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </ScrollView>
          {mode === 'minutes' && <div className={cx('placeholder-element')} />}
          <div className={cx('calendar-footer')}>
            {!isShowYearContent && (
              <Button color="primary" className={cx('confirm-btn')} onClick={handleCloseDrawer}>
                确定
              </Button>
            )}
          </div>
        </div>
      </Drawer>
    </div>
  );
};

export default Calendar;
